if (!customElements.get('skio-build-a-box')) {
  customElements.define(
    'skio-build-a-box',
    class SkioBuildABox extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.getTemplateData();
        this.fetchDynamicBoxInformation(this.templateData.productId).then((json) => {
          this.boxData = json;
          this.buildHeader();
          this.classList.remove('loading');
          this.buildProductCards();
          this.buildCart();
          this.addToCartHandler();
          this.modalHandler();
          this.repositionWidgets();
          this.setupCartVisibility();
        });
        this.trackInit();
      }

      getTemplateData() {
        const json = this.querySelector('.skio-build-a-box-template-json');
        this.templateData = JSON.parse(json.textContent);
      }

      async fetchDynamicBoxInformation(boxParentProductId) {
        return new Promise((resolve, reject) => {
          fetch('https://api.skio.com/storefront-http/get-dynamic-box', {
            method: 'POST',
            body: JSON.stringify({
              productPlatformId: boxParentProductId,
            }),
          })
            .then((response) => response.json())
            .then((response) => {
              if (!response.ok) throw new Error('Failed to get box');
              const { DynamicBox } = response;
              resolve(DynamicBox);
            })
            .catch((error) => {
              console.error(error);
              reject(error);
            });
        });
      }

      buildHeader() {
        this.querySelector('h2').innerHTML = this.boxData.settings.header;
        let filters = '';
        this.boxData.settings.productGroupingOrder.forEach((group) => {
          filters += `<button class="button button--secondary button--active-filters">${group}</button>`;
        });
        if (filters !== '') {
          filters = `<button class="button button--secondary button--active-filters active">${this.templateData.filterClearLabel}</button>${filters}`;
        }
        this.querySelector('nav').innerHTML = filters;
        Array.from(this.querySelectorAll('nav button')).forEach((filter) => {
          filter.addEventListener('click', () => {
            Array.from(this.querySelectorAll('nav button')).forEach((filter) => {
              filter.classList.remove('active');
            });
            filter.classList.add('active');
            Array.from(this.querySelectorAll('.build-a-box-grid article')).forEach((article) => {
              if (
                article.dataset.group === filter.textContent ||
                filter.textContent === this.templateData.filterClearLabel
              ) {
                article.classList.remove('hidden');
              } else {
                article.classList.add('hidden');
              }
            });
            this.trackPush('bundle_filter', {
              category: filter.textContent,
            });
          });
        });
      }

      buildProductCards() {
        const products = this.boxData.selectableProductVariants;
        const productCardTemplate = this.querySelector('.skio-build-a-box-product-card').content;
        const groups = this.boxData.settings.productGroupingOrder;
        const orderedProducts = [];
        groups.forEach((group) => {
          const groupProducts = this.boxData.settings.productGrouping[group];
          groupProducts.forEach((productId) => {
            const product = products.find((product) => product.productPlatformId.split('/').pop() === productId);
            if (product) {
              orderedProducts.push(product);
            }
          });
        });
        orderedProducts.forEach((product) => {
          product.productVariants.forEach((variant) => {
            const image = variant.imageSrc || product.productImageSrc;
            const title = product.productTitle;
            const price = this.formatMoney(variant.price * 100);
            let variantTitle =
              variant.productVariantTitle === 'Default Title' || variant.productVariantTitle === ''
                ? ''
                : variant.productVariantTitle;
            // Remove parentheses from variant title
            variantTitle = variantTitle.replace(/[()]/g, '').trim();
            const productId = product.productPlatformId.split('/').pop();
            const variantId = variant.platformId.split('/').pop();
            const group = Object.entries(this.boxData.settings.productGrouping)
              .find((group) => group[1].includes(productId))
              .shift();
            const card = productCardTemplate.cloneNode(true).querySelector('article');

            card.dataset.productHandle = product.productHandle;
            card.dataset.variantId = variantId;
            card.dataset.productId = productId;
            card.dataset.group = group;
            card.dataset.price = variant.price;
            card.querySelector('img').setAttribute('alt', `Image for ${JSON.stringify(title)}`);
            card.querySelector('h3').textContent = title;
            card.querySelector('.price').textContent = price;
            card.querySelector('.variant').textContent = variantTitle;
            const cardEl = this.querySelector('.build-a-box-grid').appendChild(card);
            const imageSrcset = `${image}&width=165&height=165 165w,${image}&width=360&height=360 360w,${image}&width=533&height=533 533w,${image}&width=720&height=720 720w,${image}&width=850&height=850 850w`;
            const imageSrc = `${image}`;
            cardEl.querySelector('img').srcset = imageSrcset;
            cardEl.querySelector('img').src = imageSrc;

            // Add price to the add-to-bundle button
            const addToBundleButton = cardEl.querySelector('.add-to-bundle');
            if (addToBundleButton) {
              const buttonLabel = addToBundleButton.dataset.add || addToBundleButton.textContent.trim();
              addToBundleButton.textContent = `${buttonLabel} ${price}`;
            }

            this.enrichCard(product, variantId);
            this.cardAddHandler(cardEl);
          });
        });
      }

      enrichCard(product, variantId) {
        fetch(`/products/${product.productHandle}?view=build-a-box-skio-product`)
          .then((response) => {
            const contentType = response.headers.get('content-type');
            if (!response.ok || !contentType || !contentType.includes('application/json')) {
              // If view doesn't exist or request fails, try fetching without view parameter
              return fetch(`/products/${product.productHandle}.json`).then((res) => {
                const fallbackContentType = res.headers.get('content-type');
                if (!res.ok || !fallbackContentType || !fallbackContentType.includes('application/json')) {
                  // Return null to indicate we couldn't get JSON data
                  return null;
                }
                return res.json();
              });
            }
            return response.json();
          })
          .then((json) => {
            // If we couldn't get JSON data, silently skip enrichment
            if (!json) {
              return;
            }

            // Only proceed if we have variants data
            if (!json.variants || !Array.isArray(json.variants)) {
              return;
            }

            const variant = json.variants.find((variant) => variant.id === parseInt(variantId));
            if (variant && !variant.available) {
              Array.from(
                this.querySelectorAll(`.build-a-box-grid article[data-product-handle="${product.productHandle}"]`)
              ).forEach((card) => {
                card.remove();
              });
              return;
            }

            // Only update subtitle if it exists in the response
            if (json.subtitle) {
              Array.from(
                this.querySelectorAll(`.build-a-box-grid article[data-product-handle="${product.productHandle}"]`)
              ).forEach((card) => {
                const h4Element = card.querySelector('h4');
                if (h4Element) {
                  h4Element.textContent = json.subtitle;
                }
              });
            }
          })
          .catch((err) => {
            // Don't remove cards on error - silently fail since enrichment is optional
            // The enrichment is just for adding subtitle and checking availability
            // If it fails, the products will still display correctly
          });
      }

      cardAddHandler(card) {
        card.querySelector('.add-to-bundle').addEventListener('click', async () => {
          const addedItems = this.getAddedItems();
          let eventName;
          let items;
          if (addedItems.find((item) => item.id === card.dataset.variantId)) {
            const slot = this.querySelector('aside article[data-variant-id="' + card.dataset.variantId + '"]');
            slot.classList.add('blank');
            slot.querySelector('img').src = '';
            slot.removeAttribute('data-variant-id');
            slot.removeAttribute('data-price');
            slot.removeAttribute('data-product-handle');
            eventName = 'bundle_remove_from_bundle';
            items = await this.trackGetItems({ productHandle: card.dataset.productHandle, id: card.dataset.variantId });
          } else {
            const slot = this.querySelector('aside article.blank');
            slot.classList.remove('blank');
            const image = card.querySelector('img');
            slot.dataset.variantId = card.dataset.variantId;
            slot.dataset.price = card.dataset.price;
            slot.dataset.productHandle = card.dataset.productHandle;
            slot.querySelector('img').src = image.src;
            eventName = 'bundle_add_to_bundle';
            items = await this.trackGetItems(card.dataset.variantId);
          }
          this.rebuildCart();
          this.trackPush(eventName, {
            currency: window.Shopify.currency.active || 'USD',
            value: this.trackGetBundlePrice(),
            in_modal: document.querySelector('.build-a-box-modal[open]') ? 'true' : 'false',
            items: items,
          });
        });
      }

      buildCart() {
        this.boxSizes = this.boxData.sizeRange || this.boxData.sizeInterval;
        this.minSize = this.boxSizes[0];
        this.maxSize = this.boxSizes[this.boxSizes.length - 1];
        if (this.boxData.sizeRange) {
          this.boxSizes = Array.from({ length: this.maxSize - this.minSize + 1 }, (v, k) => k + this.minSize);
        }
        const discounts = this.boxData.percentDiscountTier;
        let cartItems = '';
        for (let i = 1; i <= this.maxSize; i++) {
          let discount;
          let showDiscount = i >= this.minSize;
          if (showDiscount) {
            discount = `<span class="discount">${discounts[i] || Object.values(discounts).shift()}% off</span>`;
          } else {
            discount = '';
          }
          cartItems += `
              <article class="blank">
                <div>
                  <picture>
                    <img src="" alt="" sizes="(min-width: 1400px) 317px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)">
                    <img src="${this.templateData.slotImage}" alt="">
                    ${discount}
                  </picture>
                </div>
                <button>×</button>
              </article>
            `;
        }
        this.querySelector('aside .items').innerHTML = cartItems;
        Array.from(this.querySelectorAll('aside article button'), (button) => {
          button.addEventListener('click', () => {
            const card = button.closest('article');
            const variantId = card.dataset.variantId;
            this.querySelector(`.build-a-box-grid article[data-variant-id="${variantId}"] .add-to-bundle`).click();
          });
        });
      }

      rebuildCart() {
        const items = this.getAddedItems();
        Array.from(this.querySelectorAll('aside article')).forEach((slot, index) => {
          if (items[index]) {
            slot.classList.remove('blank');
            slot.querySelector('img').src = items[index].image;
            slot.dataset.variantId = items[index].id;
            slot.dataset.price = items[index].price;
          } else {
            slot.classList.add('blank');
            slot.querySelector('img').src = '';
            slot.removeAttribute('data-variant-id');
            slot.removeAttribute('data-price');
          }
        });

        let totalPrice = '';
        let savingsText = '';
        let originalPrice = '';
        if (items.length > 0) {
          totalPrice = items
            .map((item) => item.price)
            .reduce((partialSum, price) => parseFloat(partialSum) + parseFloat(price), 0);
          originalPrice = this.formatMoney(totalPrice * 100);
          const discountPercent = this.boxData.percentDiscountTier[items.length];
          if (discountPercent) {
            const discountAmount = totalPrice * (parseFloat(discountPercent) / 100);
            totalPrice = totalPrice - discountAmount;
            savingsText = this.templateData.savingsText.replace('[[savings]]', `${parseInt(discountPercent)}%`);
          }
          const finalPrice = this.formatMoney(totalPrice * 100);
          if (originalPrice !== finalPrice) {
            totalPrice = `${finalPrice} <del>${originalPrice}</del>`;
          } else {
            totalPrice = finalPrice;
          }
        }
        let priceElement = this.querySelector('.skio-build-a-box-price');
        if (!priceElement) {
          // Create the element if it doesn't exist
          const cartElement = this.querySelector('.skio-build-a-box-cart');
          if (cartElement) {
            priceElement = document.createElement('div');
            priceElement.className = 'skio-build-a-box-price';
            const addToCartButton = cartElement.querySelector('.add-to-cart');
            if (addToCartButton) {
              cartElement.insertBefore(priceElement, addToCartButton);
            } else {
              cartElement.appendChild(priceElement);
            }
          }
        }
        if (priceElement) {
          priceElement.innerHTML = totalPrice;
        }
        const savingsElement = this.querySelector('.skio-build-a-box-cart__savings');
        if (savingsElement) {
          if (items.length === 0) {
            // Show "Start Saving" when bundle is empty
            savingsElement.textContent = 'Start Saving';
            savingsElement.style.display = '';
          } else {
            // Hide the element when items are added
            savingsElement.style.display = 'none';
          }
        }

        this.querySelector('aside .add-to-cart [data-items]').dataset.items = items.length;
        if (this.boxSizes.includes(items.length)) {
          this.querySelector('aside .add-to-cart').disabled = false;
        } else {
          this.querySelector('aside .add-to-cart').disabled = true;
        }

        const addedIds = items.map((item) => item.id);
        Array.from(this.querySelectorAll('.build-a-box-grid article[data-variant-id]')).forEach((card) => {
          const addToBundleButton = card.querySelector('.add-to-bundle');
          const priceElement = card.querySelector('.price');
          const price = priceElement ? priceElement.textContent.trim() : '';
          const buttonLabel = addToBundleButton ? addToBundleButton.dataset.add || '' : '';
          const removeLabel = addToBundleButton ? addToBundleButton.dataset.remove || 'Remove' : 'Remove';

          if (addedIds.includes(card.dataset.variantId)) {
            card.classList.add('added');
            // Update button text to show remove label (without price)
            if (addToBundleButton) {
              addToBundleButton.textContent = removeLabel;
            }
          } else {
            card.classList.remove('added');
            // Restore button text with price when item is removed
            if (addToBundleButton && price && buttonLabel) {
              addToBundleButton.textContent = `${buttonLabel} ${price}`;
            }
          }
        });

        if (items.length === this.maxSize) {
          Array.from(this.querySelectorAll('.build-a-box-grid article:not(.added)')).forEach((card) => {
            card.classList.add('disabled');
          });
        } else {
          Array.from(this.querySelectorAll('.build-a-box-grid article.disabled')).forEach((card) => {
            card.classList.remove('disabled');
          });
        }
      }

      getAddedItems() {
        const addedItems = [];
        Array.from(this.querySelectorAll('aside article:not(.blank)')).forEach((item) => {
          addedItems.push({
            id: item.dataset.variantId,
            image: item.querySelector('img')?.src,
            price: item.dataset.price,
            productHandle: item.dataset.productHandle,
          });
        });
        return addedItems;
      }

      addToCartHandler() {
        this.querySelector('.add-to-cart').addEventListener('click', async () => {
          const largestBoxIndex = await fetch('/cart.json')
            .then((res) => res.json())
            .then((json) => {
              const boxesIndexes = json.items
                .filter((item) => item.properties?._dynamicBoxIndex !== undefined)
                .map((item) => item.properties?._dynamicBoxIndex);
              let largestBoxIndex = 0;
              if (boxesIndexes.length > 0) {
                largestBoxIndex = Math.max.apply(0, boxesIndexes);
              }
              return largestBoxIndex;
            });

          const items = this.getAddedItems();
          const payload = {
            items: [],
          };

          items.forEach((item) => {
            payload.items.push({
              id: item.id,
              quantity: 1,
              properties: {
                _dynamicBoxIndex: largestBoxIndex + 1,
                _dynamicBoxId: this.boxData.boxId,
              },
            });
          });

          fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          })
            .then((response) => response.json())
            .then(async (json) => {
              if (this.templateData.showModal == 'true') {
                this.subscriptionUpsell(json);
              }
              // this.subscriptionUpsell(json)
              this.trackPush('bundle_add_to_cart', {
                currency: window.Shopify.currency.active || 'USD',
                value: this.trackGetBundlePrice(),
                items: await this.trackGetItems(),
              });
            });
        });
      }

      async subscriptionUpsell(addedItems) {
        const sellingPlanGroup = this.boxData.sellingPlanGroup;
        if (!sellingPlanGroup) return;
        const sellingPlanId = Object.keys(sellingPlanGroup).shift().split('/').pop();
        const dynamicBoxIndex = addedItems.items[0]?.properties._dynamicBoxIndex;

        const modalEl = document.querySelector('.build-a-box-modal--upsell');
        modalEl.show();
        const modalRejectEl = modalEl.querySelector('.dismiss-button:not([data-bound])');
        if (modalRejectEl) {
          modalRejectEl.addEventListener('click', async () => {
            modalEl.hide();
            this.trackPush('bundle_subscription_upsell_reject', {
              currency: window.Shopify.currency.active || 'USD',
              value: this.trackGetBundlePrice(),
              items: await this.trackGetItems(),
            });
          });
          modalRejectEl.dataset.bound = true;
        }

        const getLineItems = async () => {
          return fetch('/cart.js')
            .then((res) => res.json())
            .then((cartJson) => {
              const cartItems = cartJson.items.filter((item) => item.properties?._dynamicBoxIndex === dynamicBoxIndex);
              return cartItems;
            });
        };

        const removeItems = async (remove) => {
          return await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ updates: remove }),
          });
        };

        const addItemsWithSellingPlan = async (add) => {
          return await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(add),
          })
            .then((response) => {
              return response.json();
            })
            .catch((error) => {
              console.error('Error:', error);
            });
        };

        const switchToSubscriptionButton = document.querySelector('.switch-to-subscription-button:not([data-bound])');
        if (switchToSubscriptionButton) {
          switchToSubscriptionButton.addEventListener('click', async () => {
            const cartItems = await getLineItems(addedItems.items);

            const remove = {};
            const add = {
              items: [],
            };
            cartItems.forEach((item) => {
              remove[item.key] = 0;
              add.items.push({
                id: item.id,
                properties: item.properties,
                quantity: item.quantity,
                selling_plan: sellingPlanId,
              });
            });

            await removeItems(remove);
            await addItemsWithSellingPlan(add);
            modalEl.hide();

            const eventData = {
              currency: window.Shopify.currency.active || 'USD',
              value: this.trackGetBundlePrice(),
              items: await this.trackGetItems(),
            };
            const sellingPlan = Object.values(sellingPlanGroup).shift();
            let discountPercentage;
            if (sellingPlan.priceAdjustmentType === 'PERCENTAGE') {
              discountPercentage = sellingPlan.priceAdjustmentAmount;
            }
            eventData.value = parseFloat(eventData.value - eventData.value * (discountPercentage / 100)).toFixed(2);
            eventData.items.forEach((item) => {
              item.price = parseFloat(item.price - item.price * (discountPercentage / 100)).toFixed(2);
            });

            this.trackPush('bundle_subscription_upsell_accept', eventData);
          });
          switchToSubscriptionButton.dataset.bound = true;
        }
      }

      modalHandler() {
        Array.from(this.querySelectorAll('.product-modal-trigger')).forEach((trigger) => {
          trigger.addEventListener('click', () => {
            const card = trigger.closest('article');
            const handle = card.dataset.productHandle;
            const productId = card.dataset.productId;
            const variantId = card.dataset.variantId;
            const modal = document.querySelector('.build-a-box-modal');
            modal.querySelector('.build-a-box__content-info').style.visibility = 'hidden';
            fetch(`/products/${handle}?view=build-a-box-skio-modal&variant_id=${variantId}`)
              .then((res) => res.text())
              .then((text) => {
                const html = new DOMParser().parseFromString(text, 'text/html');
                modal.querySelector('.build-a-box__content-info').innerHTML =
                  html.querySelector('.build-a-box-modal-content').outerHTML;
                modal.querySelector('.build-a-box__content-info').style.visibility = 'visible';
                if (card.classList.contains('added')) {
                  modal.classList.add('added');
                } else {
                  modal.classList.remove('added');
                }
                const price = card.querySelector('p');
                const addToBundle = card.querySelector('.add-to-bundle');
                const productInfo = modal.querySelector('.build-a-box__content-info .build-a-box-modal__product-info');
                productInfo.append(price.cloneNode(true));
                productInfo.append(addToBundle.cloneNode(true));
                const addToBundleModal = modal.querySelector(
                  '.build-a-box__content-info .build-a-box-modal__product-info .add-to-bundle'
                );
                addToBundleModal.addEventListener('click', () => {
                  this.querySelector(`article[data-variant-id="${variantId}"] .add-to-bundle`).click();
                  modal.hide();
                });
                window.pwr =
                  window.pwr ||
                  function () {
                    (pwr.q = pwr.q || []).push(arguments);
                  };
                pwr('render', {
                  api_key: 'd471d1a1-2dfd-4df5-825c-701da2aef8cd',
                  locale: 'en_US',
                  merchant_group_id: '53052543',
                  merchant_id: '1917124082',
                  page_id: productId,
                  review_wrapper_url: `https://www.vitacup.com/pages/write-a-review?pr_page_id=${productId}`,
                  components: {
                    ReviewSnippet: 'pr-reviewsnippet',
                  },
                });

                (async () => {
                  this.trackPush('bundle_view_item', {
                    currency: window.Shopify.currency.active || 'USD',
                    value: 0,
                    items: await this.trackGetItems({ productHandle: handle, id: variantId }),
                  });
                })();
              });
          });
        });
      }

      trackInit() {
        window.dataLayer = window.dataLayer || [];
      }

      trackPush(event, parameters) {
        if (typeof gtag !== 'function') return;

        if (parameters.items) {
          parameters.items.forEach((item, index) => {
            for (const [key, value] of Object.entries(item)) {
              parameters[`item-${index + 1}--${key}`] = value;
            }
          });
        }
        gtag('event', event, parameters);
      }

      trackGetBundlePrice() {
        const items = this.getAddedItems();
        let totalPrice;
        if (items.length > 0) {
          totalPrice = items
            .map((item) => item.price)
            .reduce((partialSum, price) => parseFloat(partialSum) + parseFloat(price), 0);
          const discountPercent = this.boxData.percentDiscountTier[items.length];
          if (discountPercent) {
            const discountAmount = totalPrice * (parseFloat(discountPercent) / 100);
            totalPrice = totalPrice - discountAmount;
          }
          totalPrice = parseFloat(totalPrice).toFixed(2);
        }
        return totalPrice;
      }

      async trackGetItems(filter) {
        let items = this.getAddedItems();
        if (filter?.productHandle && filter?.id) {
          // if filter is an object (containing productHandle and id)
          items = [filter];
        }
        const detailedItems = [];
        const promises = [];
        items.forEach((item, index) => {
          if (filter && !isNaN(filter) && filter !== item.id) return; // if filter is a variant id
          const productFetch = fetch(`/products/${item.productHandle}.json`)
            .then((res) => res.json())
            .then((json) => {
              const title = json.product.title;
              const variant = json.product.variants.find((variant) => variant.id === parseInt(item.id));
              const discountPercent = this.boxData.percentDiscountTier[items.length];
              let discountAmount = 0;
              let price = variant.price;
              if (discountPercent) {
                discountAmount = parseFloat(price * (parseFloat(discountPercent) / 100)).toFixed(2);
                price = parseFloat(price - discountAmount).toFixed(2);
              }
              const details = {
                item_id: variant.sku,
                item_name: title,
                discount: discountAmount,
                item_variant: variant.title,
                price: price,
              };
              if (!filter || !isNaN(filter)) {
                details.index = index + 1;
              }
              detailedItems.push(details);
            });
          promises.push(productFetch);
        });
        await Promise.all(promises);
        return detailedItems;
      }

      formatMoney(cents, format) {
        if (typeof cents == 'string') {
          cents = cents.replace('.', '');
        }
        var value = '';
        var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
        var formatString = format || '${{amount}}';

        function defaultOption(opt, def) {
          return typeof opt == 'undefined' ? def : opt;
        }

        function formatWithDelimiters(number, precision, thousands, decimal) {
          precision = defaultOption(precision, 2);
          thousands = defaultOption(thousands, ',');
          decimal = defaultOption(decimal, '.');

          if (isNaN(number) || number == null) {
            return 0;
          }

          number = (number / 100.0).toFixed(precision);

          var parts = number.split('.'),
            dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
            cents = parts[1] ? decimal + parts[1] : '';

          return dollars + cents;
        }

        var match = formatString.match(placeholderRegex);

        if (match) {
          switch (match[1]) {
            case 'amount':
              value = formatWithDelimiters(cents, 2);
              break;
            case 'amount_no_decimals':
              value = formatWithDelimiters(cents, 0);
              break;
            case 'amount_with_comma_separator':
              value = formatWithDelimiters(cents, 2, '.', ',');
              break;
            case 'amount_no_decimals_with_comma_separator':
              value = formatWithDelimiters(cents, 0, '.', ',');
              break;
          }
        }

        return formatString.replace(placeholderRegex, value);
      }

      repositionWidgets() {
        const reposition = () => {
          // const atcHeight = this.querySelector('aside').clientHeight + 10 + 'px';
          let atcHeight = '100vh';
          if (window.matchMedia('(min-width: 750px)').matches) {
            atcHeight = '0px';
          }
          const atcHeightWithOffset = `calc(${atcHeight} + 10px)`;

          document.head.insertAdjacentHTML(
            'beforeend',
            `
            <style>
              access-widget-ui {
                --offset-top: ${atcHeightWithOffset}!important;
              }
              iframe#chat-button {
                bottom: ${atcHeightWithOffset}!important;
              }
            </style>
          `
          );
        };
        if (document.readyState === 'loading') {
          window.addEventListener('load', () => {
            reposition();
          });
        } else {
          reposition();
        }
      }

      setupCartVisibility() {
        // Only apply on mobile (749px or less)
        if (window.matchMedia('(max-width: 749px)').matches) {
          const mainElement = this.querySelector('.build-a-box-main');
          const cartElement = this.querySelector('.skio-build-a-box-cart');

          if (!mainElement || !cartElement) return;

          // Check initial state
          const isInitiallyVisible =
            mainElement.getBoundingClientRect().top < window.innerHeight &&
            mainElement.getBoundingClientRect().bottom > 0;

          if (isInitiallyVisible) {
            cartElement.classList.add('cart-visible');
          }

          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  // Main element is in viewport - slide cart in
                  cartElement.classList.add('cart-visible');
                } else {
                  // Main element is out of viewport - slide cart out
                  cartElement.classList.remove('cart-visible');
                }
              });
            },
            {
              threshold: 0,
              rootMargin: '0px',
            }
          );

          observer.observe(mainElement);

          // Store observer for cleanup
          this._cartVisibilityObserver = observer;
        }
      }

      disconnectedCallback() {
        // Clean up observer when element is removed from DOM
        if (this._cartVisibilityObserver) {
          this._cartVisibilityObserver.disconnect();
          this._cartVisibilityObserver = null;
        }
      }
    }
  );
}
